import { Component } from '@angular/core';
import {
  IPatientTableItem,
  IPDQmResponse,
} from 'src/app/interfaces/PDQ/IPDQResponse';
import { PDQService } from 'src/app/services/pdq.service';
import { SwalToastService } from 'src/app/services/swal.service';
import * as xml2js from 'xml2js';

@Component({
  selector: 'app-pdqm',
  templateUrl: './pdqm.component.html',
  styleUrls: ['./pdqm.component.css'],
  standalone: false,
})
export class PdqmComponent {
  ip: string = '10.30.8.1';
  port: number = 8800;

  value = '';

  isXmlFormat: boolean = false;

  patientsData: IPatientTableItem[] = [];

  searchParams = {
    family: '',
    given: '',
    birthdate: '',
    gender: '',
    city: '',
    identifierSystem: '',
    identifierValue: '',
  };

  nextPageUrl: string | null = null;

  constructor(
    private readonly pdqservice: PDQService,
    private readonly swalToastService: SwalToastService
  ) {}

  pdqm() {
    const format = this.isXmlFormat ? 'xml' : 'json';

    const queryParts: string[] = [];

    if (this.searchParams.family) {
      queryParts.push(`family=${encodeURIComponent(this.searchParams.family)}`);
    }
    if (this.searchParams.given) {
      queryParts.push(`given=${encodeURIComponent(this.searchParams.given)}`);
    }
    if (this.searchParams.birthdate) {
      queryParts.push(
        `birthdate=${encodeURIComponent(this.searchParams.birthdate)}`
      );
    }
    if (this.searchParams.gender) {
      queryParts.push(`gender=${encodeURIComponent(this.searchParams.gender)}`);
    }
    if (this.searchParams.city) {
      queryParts.push(
        `address-city=${encodeURIComponent(this.searchParams.city)}`
      );
    }

    if (
      this.searchParams.identifierSystem &&
      !this.searchParams.identifierValue
    ) {
      queryParts.push(
        `identifier=${encodeURIComponent(this.searchParams.identifierSystem)}`
      );
    }

    if (
      this.searchParams.identifierValue &&
      !this.searchParams.identifierSystem
    ) {
      queryParts.push(
        `identifier=${encodeURIComponent(this.searchParams.identifierValue)}`
      );
    }

    if (
      this.searchParams.identifierSystem &&
      this.searchParams.identifierValue
    ) {
      const identifier = `${this.searchParams.identifierSystem}|${this.searchParams.identifierValue}`;
      queryParts.push(`identifier=${encodeURIComponent(identifier)}`);
    }

    //NORMAL
    // const query = 'Patient?' + queryParts.join('&') + `&_format=${format}`;

    const query = 'PDQm4?' + queryParts.join('&') + `&_format=${format}`;
    //PAGING
    // const query = 'Patient?' + queryParts.join('&') + `&_count=10&_format=${format}`;

    // const query = 'r4?' + queryParts.join('&') + `&_format=${format}`;
    this.value = query; // optional: to display it in the textarea

    this.pdqservice
      .pdqm(this.ip, this.port, query, this.isXmlFormat)
      .subscribe({
        next: (response) => {
          if (this.isXmlFormat && typeof response === 'string') {
            this.convertXmlToJson(response)
              .then((parsed: IPDQmResponse) => {
                const cleanJson = this.unwrapFhirValues(parsed);
                this.handlePdqResponse(cleanJson as IPDQmResponse);
              })
              .catch((err) => {
                this.swalToastService.toastError(
                  'Failed to parse XML response'
                );
                console.error('XML parsing error:', err);
                this.patientsData = [];
              });
          } else {
            this.handlePdqResponse(response as IPDQmResponse);
          }
        },
        error: (err) => {
          this.swalToastService.toastError('Unable to get PDQm');
          console.error('Error sending PDQm:', err);
          this.patientsData = [];
        },
      });
  }

  handlePdqResponse(response: IPDQmResponse) {
    console.log('Parsed Response:', response);

    if (!response || !Array.isArray(response.entry)) {
      this.patientsData = [];
      return;
    }

    this.nextPageUrl =
      response.link?.find((l) => l.relation === 'next')?.url || null;

    this.patientsData = response.entry
      .map((entry) => {
        const res = entry?.resource;
        if (!res) return null;

        const nameObj = res.name?.[0];
        const givenNames = nameObj?.given;
        const fullName = nameObj
          ? `${(Array.isArray(givenNames) ? givenNames : [givenNames]).join(
              ' '
            )} ${nameObj.family || ''}`.trim()
          : '';

        const identifiers =
          res.identifier?.map((id) => `${id.system}: ${id.value}`).join('; ') ||
          '';

        const addressObj = res.address?.[0];
        const lines = addressObj?.line;
        const addressStr = addressObj
          ? `${(Array.isArray(lines) ? lines : [lines]).join(' ')}, ${
              addressObj.city || ''
            }, ${addressObj.state || ''}, ${addressObj.postalCode || ''}, ${
              addressObj.country || ''
            }`.trim()
          : '';

        return {
          name: fullName,
          gender: res.gender || '',
          birthDate: res.birthDate || '',
          identifiers,
          address: addressStr,
          link: entry.fullUrl || '', // ✅ include the fullUrl here
        };
      })
      .filter((item): item is IPatientTableItem => item !== null);

    console.log('Mapped Table Items:', this.patientsData);
  }

  unwrapFhirValues(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.unwrapFhirValues(item));
    }

    if (typeof obj === 'object' && obj !== null) {
      const keys = Object.keys(obj);
      if (keys.length === 1 && keys[0] === 'value') {
        return obj.value;
      }

      const result: any = {};
      for (const key of keys) {
        result[key] = this.unwrapFhirValues(obj[key]);
      }
      return result;
    }

    return obj;
  }

  convertXmlToJson(xml: string): Promise<IPDQmResponse> {
    console.log('Original XML Response:', xml);

    const parser = new xml2js.Parser({
      explicitArray: false,
      ignoreAttrs: false,
      mergeAttrs: true,
      tagNameProcessors: [xml2js.processors.stripPrefix],
      attrNameProcessors: [xml2js.processors.stripPrefix],
    });

    return parser.parseStringPromise(xml).then((result) => {
      const bundle = result.Bundle;
      bundle.resourceType = 'Bundle';

      bundle.entry = Array.isArray(bundle.entry)
        ? bundle.entry
        : [bundle.entry];

      bundle.entry = bundle.entry
        .map((e: any) => {
          const patient = e?.resource?.Patient;

          if (!patient || typeof patient !== 'object') {
            console.warn('Skipping invalid entry without Patient resource:', e);
            return null;
          }

          patient.resourceType = 'Patient';

          if (patient.identifier && !Array.isArray(patient.identifier)) {
            patient.identifier = [patient.identifier];
          }
          if (patient.name && !Array.isArray(patient.name)) {
            patient.name = [patient.name];
          }
          if (patient.address && !Array.isArray(patient.address)) {
            patient.address = [patient.address];
          }

          e.resource = patient;
          return e;
        })
        .filter((e: any) => e !== null);

      return bundle as IPDQmResponse;
    });
  }

  loadNextPage() {
    if (!this.nextPageUrl) return;

    this.pdqservice.fetchNextPage(this.nextPageUrl).subscribe({
      next: (res) => {
        console.log(this.nextPageUrl);
        if (this.isXmlFormat) {
          this.convertXmlToJson(res).then((parsed) => {
            const clean = this.unwrapFhirValues(parsed);
            this.handlePdqResponse(clean);
          });
        } else {
          console.log(this.nextPageUrl);
          this.handlePdqResponse(JSON.parse(res));
        }
      },
      error: (err) => {
        console.log(this.nextPageUrl);
        this.swalToastService.toastError('Failed to fetch next page');
        console.error('Paging error:', err);
      },
    });
  }
}
